import React, { useEffect, useState } from 'react';
import { useOnlineStatus, pendingActions } from '@/lib/offlineService';
import { Wifi, WifiOff, CloudOff, RefreshCw, Check } from 'lucide-react';
import { toast } from 'sonner';

const OfflineIndicator = ({ onSync }) => {
  const isOnline = useOnlineStatus();
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const updateCount = () => {
      setPendingCount(pendingActions.count());
    };
    
    updateCount();
    window.addEventListener('storage', updateCount);
    window.addEventListener('pending-actions-updated', updateCount);
    
    return () => {
      window.removeEventListener('storage', updateCount);
      window.removeEventListener('pending-actions-updated', updateCount);
    };
  }, []);

  useEffect(() => {
    if (!isOnline) {
      setShowBanner(true);
    } else if (pendingCount > 0) {
      handleSync();
    } else {
      const timer = setTimeout(() => setShowBanner(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, pendingCount]);

  const handleSync = async () => {
    if (!isOnline || syncing) return;
    
    setSyncing(true);
    try {
      if (onSync) {
        const results = await onSync();
        if (results.success > 0) {
          toast.success(`${results.success} action(s) synchronisée(s)`);
        }
        if (results.failed > 0) {
          toast.error(`${results.failed} action(s) en échec`);
        }
      }
      setPendingCount(pendingActions.count());
    } catch (error) {
      toast.error('Erreur de synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const shouldShowFloating = !isOnline || pendingCount > 0;
  
  return (
    <>
      {/* Top Banner */}
      {showBanner && (
        <div className={`fixed top-0 left-0 right-0 z-50 ${
          !isOnline ? 'bg-amber-500' : pendingCount > 0 ? 'bg-blue-500' : 'bg-emerald-500'
        }`}>
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              {!isOnline ? (
                <>
                  <WifiOff className="w-4 h-4" />
                  <span className="text-sm">Mode hors ligne - Les données sont sauvegardées localement</span>
                </>
              ) : pendingCount > 0 ? (
                <>
                  <CloudOff className="w-4 h-4" />
                  <span className="text-sm">{pendingCount} action(s) en attente de synchronisation</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span className="text-sm">Synchronisé</span>
                </>
              )}
            </div>
            
            {isOnline && pendingCount > 0 && (
              <button
                onClick={handleSync}
                disabled={syncing}
                className="flex items-center gap-1 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full text-sm transition-colors"
              >
                <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} />
                Synchroniser
              </button>
            )}
            
            {isOnline && pendingCount === 0 && (
              <button
                onClick={() => setShowBanner(false)}
                className="text-white/80 hover:text-white"
              >
                ✕
              </button>
            )}
          </div>
        </div>
      )}

      {/* Floating Indicator */}
      <div className={`fixed bottom-20 right-4 z-50 transition-all duration-300 ${
        shouldShowFloating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
      }`}>
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg ${
          !isOnline ? 'bg-amber-500 text-white' : 'bg-blue-500 text-white'
        }`}>
          {!isOnline ? (
            <>
              <WifiOff className="w-4 h-4" />
              <span className="text-sm font-medium">Hors ligne</span>
            </>
          ) : pendingCount > 0 ? (
            <>
              <CloudOff className="w-4 h-4" />
              <span className="text-sm font-medium">{pendingCount} en attente</span>
              <button
                onClick={handleSync}
                disabled={syncing}
                className="ml-2 p-1 hover:bg-white/20 rounded-full transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              </button>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default OfflineIndicator;
